<template>
  <page-template :show-loader="false">
    <template #pageHeadline>
      {{ t('aiManagement.pageHeadline') }}
    </template>
    <template #secondLevelHeaderEntire>
      <rbz-filter
        ref="rbzFilter"
        :search-disabled="isTimelineLoading"
        :default-time="defaultTime"
        :query-history="queryHistory"
        :is-query-history-loading="false"
        :show-query-history="false"
        @search="loadData"
      >
        <template #additionalButtons>
          <rbz-form-button
            variant="tertiary"
            icon="IconOpenNew"
            data-qa="docs-button"
            class="docs-button"
            @button-click="openDocumentation"
            v-b-tooltip="t('aiManagement.documentation')"
            :ignore-update-in-process="true"
          />
        </template>
      </rbz-filter>
    </template>
    <template #pageContent>
      <div class="ai-management-container">
        <div class="ai-management-requests">
          <span class="ai-management-requests-title rbz-text-large-medium">{{
            t('aiManagement.requests')
          }}</span>
          <skeleton
            :loading="isInitialLoad && isTimelineLoading"
            :refreshing="isTimelineLoading && !isInitialLoad"
            type="stat-cards"
            :count="4"
          >
            <div class="ai-management-requests-cards">
              <rbz-stat-card
                v-for="card in cardRequests"
                :key="card.title"
                :title="card.title"
                :content="card.content"
                :icon="card.icon"
                :hover-text="card.hoverText"
              />
            </div>
          </skeleton>
        </div>

        <div class="ai-management-graph">
          <div class="rbz-card white chart-panel ai-management-graph-chart">
            <div class="chart-panel-header rbz-text-large">
              {{ t('aiManagement.graph') }}
            </div>
            <div class="chart-panel-body big-chart-panel-body">
              <!-- Chart Skeleton for loading -->
              <skeleton v-show="isTimelineLoading" :loading="true" type="chart" />
              <rbz-chart
                v-show="!isTimelineLoading && trafficChartData.length > 0"
                :data="trafficChartData"
                :series-options="trafficChartSeriesOptions"
                :chart-height="370"
                :legend-sum="legendSumConfig"
                :legend-as-tooltip="legendAsTooltipConfig"
                sync-key="aiManagement"
              />
              <!-- No data message -->
              <div
                v-show="!isTimelineLoading && trafficChartData.length === 0"
                class="no-data-message"
              >
                {{ t('dashboard.noSearchResultsMessage') }}
              </div>
            </div>
          </div>
        </div>

        <skeleton
          :loading="isInitialLoad && isTimelineLoading"
          :refreshing="isTimelineLoading && !isInitialLoad"
          type="table"
          :rows="5"
          :columns="8"
        >
          <ai-management-table
            :ai-tools-stats="aiToolsStats"
            :enabled-crawlers="enabledCrawlers"
            :global-filters="globalFilters"
            :actions="actions"
            :selected-branch="selectedBranch"
            @toggle-crawler="toggleCrawler"
            @hide-others="hideOthers"
            @show-all="showAll"
            @refresh-filters="loadGlobalFilters"
          />
        </skeleton>
      </div>
    </template>
  </page-template>
</template>

<script setup lang="ts">
import PageTemplate from '@/views/PageTemplate.vue';
import { computed, nextTick, onMounted, ref } from 'vue';
import RbzStatCard from '@/components/RbzStatCard.vue';
import RbzChart, { SeriesOptions } from 'ui-component-library/rbz/components/RbzChart.vue';
import RbzFilter from '@/components/RbzFilter.vue';
import RbzFormButton from '@/components/RbzFormButton.vue';
import AiManagementTable from '@/components/AiManagementTable.vue';
import Skeleton from '@/components/Skeleton.vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import IconInfoSmall from 'ui-component-library/assets/icons/IconInfoSmall.vue';
import { AiToolStat, TimelineItem, ChartDataPoint, AiGlobalFilter, AiAction } from '@/types';
import { useBranchesStore } from '@/store/BranchesStore';
import axios, { AxiosError, AxiosResponse } from 'axios';
import Utils from 'ui-component-library/helpers/Utils';
import RequestsUtils from '@/helpers/RequestsUtils';
import { shouldUseMockData, getAiManagementMockData } from '@/helpers/MockDataUtils';
import { AI_MANAGEMENT_DOCS_URL } from '@/types/externalUrls';
import { isExcludedTag } from '@/types/const';
import { ENV } from '@/constants';

const branchesStore = useBranchesStore();
const { t } = useI18n();
const route = useRoute();
const isTimelineLoading = ref(false);
const defaultTime = ref(5);
const queryHistory = ref([]);
const actionRequired = ref(true);
const cancelSource = ref(axios.CancelToken.source());
const currentQuery = ref('');
const rbzFilter = ref<InstanceType<typeof RbzFilter> | null>(null);

const selectedBranch = computed(() => branchesStore.selectedBranchId);

const timeline = ref<TimelineItem[]>([]);
const aiToolsStats = ref<AiToolStat[]>([]);
const enabledCrawlers = ref<Record<string, boolean>>({});
const globalFilters = ref<AiGlobalFilter[]>([]);
const actions = ref<AiAction[]>([]);

const calculatedRequests = computed(() => {
  let total = 0;
  let passed = 0;
  let blocked = 0;
  let referralVisits = 0;

  for (const item of aiToolsStats.value) {
    if (isExcludedTag(item.tag)) continue;
    if (enabledCrawlers.value[item.tag] === false) continue;

    total += item.total_requests || 0;
    passed += item.total_passed || 0;
    blocked += item.total_blocked || 0;
    referralVisits += item.total_referrals || 0;
  }

  return { total, passed, blocked, referralVisits };
});

const cardRequests = computed(() => {
  const totals = calculatedRequests.value;

  return [
    {
      title: t('aiManagement.totalRequests'),
      content: Utils.amountSuffixFormatter(totals.total, t),
      icon: IconInfoSmall,
      hoverText: t('aiManagement.totalRequestsHover'),
    },
    {
      title: t('aiManagement.passedRequests'),
      content: Utils.amountSuffixFormatter(totals.passed, t),
      icon: IconInfoSmall,
      hoverText: t('aiManagement.passedRequestsHover'),
    },
    {
      title: t('aiManagement.blockedRequests'),
      content: Utils.amountSuffixFormatter(totals.blocked, t),
      icon: IconInfoSmall,
      hoverText: t('aiManagement.blockedRequestsHover'),
    },
    {
      title: t('aiManagement.referralVisits'),
      content: Utils.amountSuffixFormatter(totals.referralVisits, t),
      icon: IconInfoSmall,
      hoverText: t('aiManagement.referralVisitsHover'),
    },
  ];
});

const trafficChartData = computed((): ChartDataPoint[] =>
  timeline.value.map((item) => {
    const hits = item.num_of_requests || 0;
    const blocked = item.num_of_blocked_requests || 0;
    const originBlocked = item.num_of_origin_blocked_requests || 0;
    const challenge = item.num_of_challenges || 0;

    return {
      timeframe: item.time_period * 1000,
      hits,
      blocked,
      originBlocked,
      challenge,
      passed: Math.max(hits - blocked - originBlocked - challenge, 0),
    };
  })
);

const trafficChartSeriesOptions = computed((): SeriesOptions[] => {
  const totalRequests: SeriesOptions = {
    key: 'Total Requests',
    title: t('aiManagement.totalRequests'),
    fieldName: 'hits',
    show: true,
    drawStyle: 'spline',
    strokeColor: '#28a745',
  };

  const seriesOptions: SeriesOptions[] = [totalRequests];

  return seriesOptions.filter((series) =>
    trafficChartData.value.some((item) => item[series.fieldName] > 0)
  );
});

const isInitialLoad = computed(() => aiToolsStats.value.length === 0);

const resetState = () => {
  // Don't clear data during refresh - only clear on explicit reset
  actionRequired.value = false;
};

const ensureFilter = (filters: string[], filterToEnsure: string): void => {
  // Check if the exact filter already exists
  const exists = filters.some((f) => f.trim() === filterToEnsure.trim());

  if (!exists) {
    filters.push(filterToEnsure);
  }
};

const prepareQuery = (query: string): string => {
  const filters = query.trim().split(/, /).filter(Boolean);

  ensureFilter(filters, 'tags="ai-dashboard"');
  ensureFilter(filters, `branch=${selectedBranch.value}`);

  return filters.join(', ');
};

const cancelPreviousRequest = () => {
  cancelSource.value.cancel('Operation cancelled because a new search has began');
  cancelSource.value = axios.CancelToken.source();
};

const fetchTimelineData = async (query: string) => {
  return RequestsUtils.sendRequest({
    methodName: 'GET',
    url: `data/timeline?filters=${encodeURIComponent(query)}`,
    config: {
      headers: { provider: ENV.VITE_LOGS_DB_PROVIDER, syntax: 'string_query' },
      cancelToken: cancelSource.value.token,
    },
    onFail: (error: AxiosError) => {
      Utils.toast({
        message: (error.response?.data as { detail?: string })?.detail || '',
        type: 'is-danger',
      });
    },
  });
};

const applyMockData = () => {
  setTimeout(() => {
    const mockData = getAiManagementMockData();
    timeline.value = mockData.timeline || [];
    aiToolsStats.value = mockData.ai_tools_stats || [];

    // Sync crawler state from query filters after data is loaded
    const queryFilters = currentQuery.value ? currentQuery.value.split(/, /).filter(Boolean) : [];
    const hasCrawlerTagFilter = queryFilters.some(isCrawlerTagsFilter);

    if (!hasCrawlerTagFilter) {
      initializeEnabledCrawlers();
    } else {
      // Parse negative filters and set disabled state
      syncCrawlerStateFromQuery(queryFilters);
      // Then initialize any new crawlers that weren't in the query
      initializeEnabledCrawlers();
    }

    loadGlobalFilters();
    loadActions();

    isTimelineLoading.value = false;
  }, 500);
};

const processTimelineResponse = (response: AxiosResponse) => {
  timeline.value = response?.data?.data?.results || [];
  aiToolsStats.value = response?.data?.data?.ai_tools_stats || [];

  // Sync crawler state from query filters after data is loaded
  const queryFilters = currentQuery.value ? currentQuery.value.split(/, /).filter(Boolean) : [];
  const hasCrawlerTagFilter = queryFilters.some(isCrawlerTagsFilter);

  if (!hasCrawlerTagFilter) {
    initializeEnabledCrawlers();
  } else {
    // Parse negative filters and set disabled state
    syncCrawlerStateFromQuery(queryFilters);
    // Then initialize any new crawlers that weren't in the query
    initializeEnabledCrawlers();
  }

  loadGlobalFilters();
  loadActions();
};

const loadData = async (query: string) => {
  resetState();
  isTimelineLoading.value = true;
  currentQuery.value = query;

  await branchesStore.loadBranches();

  if (shouldUseMockData()) {
    applyMockData();
    return;
  }

  cancelPreviousRequest();
  const finalQuery = prepareQuery(query);

  fetchTimelineData(finalQuery)
    .then(processTimelineResponse)
    .finally(() => {
      isTimelineLoading.value = false;
    });
};

const initializeEnabledCrawlers = () => {
  const newCrawlers = aiToolsStats.value
    .filter((item) => !isExcludedTag(item.tag) && enabledCrawlers.value[item.tag] === undefined)
    .reduce<Record<string, boolean>>((acc, item) => {
      acc[item.tag] = true;
      return acc;
    }, {});

  if (Object.keys(newCrawlers).length > 0) {
    enabledCrawlers.value = { ...enabledCrawlers.value, ...newCrawlers };
  }
};

const loadGlobalFilters = async () => {
  try {
    const response = await RequestsUtils.sendRequest({
      methodName: 'GET',
      url: `conf/${selectedBranch.value}/global-filters`,
      config: { headers: { 'x-fields': 'id, name, action, tags' } },
    });
    globalFilters.value = response?.data?.items || [];
  } catch (error) {
    console.error('Error loading global filters:', error);
  }
};

const loadActions = async () => {
  try {
    const response = await RequestsUtils.sendRequest({
      methodName: 'GET',
      url: `conf/${selectedBranch.value}/actions`,
      config: { headers: { 'x-fields': 'id, name, type' } },
    });
    actions.value = response?.data?.items || [];
  } catch (error) {
    console.error('Error loading actions:', error);
  }
};

// Type definition for crawler filter state
type CrawlerFilterState = {
  disabled: string[];
};

// Single serializer: UI state → query filters
const serializeCrawlerFilters = (state: CrawlerFilterState): string[] =>
  state.disabled.map((id) => `tags !="${id}"`);

// Single parser: query filters → UI state
const parseCrawlerFilters = (filters: string[]): CrawlerFilterState => ({
  disabled: filters
    .map((f) => f.match(/^tags\s*!=\s*"([^"]+)"$/))
    .filter(Boolean)
    .map((m) => m![1]),
});

const getRouteFilters = (): string[] => {
  const currentFilters = route.query.filters?.toString() || '';
  return currentFilters ? currentFilters.split(/, /).filter(Boolean) : [];
};

const isCrawlerTagsFilter = (filter: string): boolean => {
  const trimmed = filter.trim();
  if (!trimmed.startsWith('tags')) return false;
  return true;
};

const syncCrawlerStateFromQuery = (queryFilters: string[]) => {
  const crawlerState = parseCrawlerFilters(queryFilters);

  // If we have negative filters, all crawlers start enabled except the disabled ones
  if (crawlerState.disabled.length > 0) {
    const allIds = getAllCrawlerIds();
    enabledCrawlers.value = allIds.reduce<Record<string, boolean>>((acc, id) => {
      acc[id] = !crawlerState.disabled.includes(id);
      return acc;
    }, {});
  }
};

const applyCrawlerTagsFilter = (disabledCrawlerIds: string[]) => {
  if (!rbzFilter.value) return;

  // Remove old crawler tag filters
  const tagFiltersToRemove = getRouteFilters().filter(isCrawlerTagsFilter);
  for (const filter of tagFiltersToRemove) {
    rbzFilter.value.removeFilter(filter);
  }

  // Add new filters using serializer
  const crawlerState: CrawlerFilterState = { disabled: disabledCrawlerIds };
  const filters = serializeCrawlerFilters(crawlerState);
  for (const filter of filters) {
    rbzFilter.value.addFilter(filter);
  }
};

const getAllCrawlerIds = (): string[] => {
  return aiToolsStats.value.filter((item) => !isExcludedTag(item.tag)).map((item) => item.tag);
};

const getDisabledCrawlerIds = (): string[] => {
  return getAllCrawlerIds().filter((id) => enabledCrawlers.value[id] === false);
};

const enableAllCrawlers = () => {
  const allCrawlerIds = new Set<string>();
  for (const id of getAllCrawlerIds()) {
    allCrawlerIds.add(id);
  }
  for (const id of Object.keys(enabledCrawlers.value)) {
    if (!isExcludedTag(id)) {
      allCrawlerIds.add(id);
    }
  }

  enabledCrawlers.value = Array.from(allCrawlerIds).reduce<Record<string, boolean>>((acc, id) => {
    acc[id] = true;
    return acc;
  }, {});
};

// Centralized function to update filters and trigger search
const updateCrawlerFiltersAndSearch = (disabledIds: string[]) => {
  applyCrawlerTagsFilter(disabledIds);
  rbzFilter.value?.search(true, false);
};

const toggleCrawler = (crawlerId: string) => {
  const isCurrentlyEnabled = enabledCrawlers.value[crawlerId] !== false;

  enabledCrawlers.value = {
    ...enabledCrawlers.value,
    [crawlerId]: !isCurrentlyEnabled,
  };

  updateCrawlerFiltersAndSearch(getDisabledCrawlerIds());
};

const hideOthers = (crawlerId: string) => {
  enabledCrawlers.value = getAllCrawlerIds().reduce<Record<string, boolean>>((acc, id) => {
    acc[id] = id === crawlerId;
    return acc;
  }, {});

  updateCrawlerFiltersAndSearch(getDisabledCrawlerIds());
};

const showAll = () => {
  enableAllCrawlers();

  // Remove crawler-specific tag filters
  if (rbzFilter.value) {
    const tagFiltersToRemove = getRouteFilters().filter(isCrawlerTagsFilter);
    for (const filter of tagFiltersToRemove) {
      rbzFilter.value.removeFilter(filter);
    }
  }

  rbzFilter.value?.search(true, false);
};

const openDocumentation = () => {
  window.open(AI_MANAGEMENT_DOCS_URL, '_blank', 'noopener,noreferrer');
};

const legendSumConfig = ref({
  active: true,
});

const legendAsTooltipConfig = ref({
  active: true,
  width: 'max-content',
  showTopX: {
    startIndex: 1,
    sliceEnd: 20,
  },
});

onMounted(() => {
  nextTick(() => {
    if (rbzFilter.value) {
      rbzFilter.value.search(true, false);
    }
  });
});
</script>

<style scoped lang="scss">
@use 'sass:map';
@use 'ui-component-library/assets/styles/colors' as colors;
@use 'ui-component-library/assets/styles/main' as main;

.ai-management-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.ai-management-requests {
  display: flex;
  flex-direction: column;
  gap: 25px;
}

.ai-management-requests-cards {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  flex-direction: row;
  gap: 24px;
  min-height: 135px;
  height: 135px;

  @media (width <= 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
}

.ai-management-requests-title {
  display: flex;
  justify-content: space-between;
  align-items: stretch;
}

.ai-management-graph {
  width: 100%;
  display: block;
}

.ai-management-graph-chart {
  padding: 16px 24px;
}

.chart-panel-body {
  min-height: 220px;
  padding: 16px 24px;
  position: relative;
}

.big-chart-panel-body {
  min-height: 458px;

  > .rbz-skeleton-wrapper {
    position: absolute;
    inset: 0;
    z-index: 10;
    background: map.get(colors.$colors, 'white');
  }
}

.no-data-message {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--black, #000);
  font-size: 14px;
}

.chart-panel-header {
  padding: 16px 24px;
}

.ai-management-graph :deep(.control-buttons) {
  display: none !important;
}

.ai-management-graph :deep(.legend-wrapper .control-buttons) {
  display: none !important;
}

:deep(.buttons-wrapper) {
  .copy-query-button,
  .dropdown {
    display: none !important;
  }
}

.info-link {
  align-items: center;
  display: flex;
  padding: 8px;

  &:hover {
    opacity: 0.7;
  }

  svg {
    height: 24px;
    width: 24px;
  }
}
</style>
